import { useEffect, useId, useMemo, useRef, useState } from 'react'
import { Check, ChevronDown, Search } from 'lucide-react'

/* A field that shows what is chosen, and searches when you open it.

   Two controls rather than one, and deliberately. The field itself is a
   button: it states the selection and nothing else, so what is committed is
   never confused with what is being typed. The search box lives inside the
   panel, above the list it filters.

   That split also settles a browser problem the single-input version had.
   An <input> labelled "City" inside a form asking for an address is exactly
   what Chrome offers to autofill, and its suggestion panel drew on top of
   our own list. A button is not autofillable, and the search box carries no
   address-shaped name for the heuristic to catch. */

export type ComboboxOption = {
  value: string
  label: string
  /** Printed after the label, greyed — a region, a category. */
  hint?: string
  /** Matched but never shown: alternative spellings and abbreviations. */
  keywords?: string
}

type ComboboxProps = {
  id: string
  value: string
  onChange: (value: string) => void
  options: ComboboxOption[]
  /** Shown on the button while nothing is chosen. */
  placeholder?: string
  searchPlaceholder?: string
  /** Shown while the options are still being fetched. */
  loading?: boolean
  className?: string
  'aria-invalid'?: boolean
}

function Combobox({
  id,
  value,
  onChange,
  options,
  placeholder = 'Select',
  searchPlaceholder = 'Type to search…',
  loading = false,
  className = '',
  ...aria
}: ComboboxProps) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [active, setActive] = useState(0)

  const listId = useId()
  const root = useRef<HTMLDivElement>(null)
  const search = useRef<HTMLInputElement>(null)
  const trigger = useRef<HTMLButtonElement>(null)

  const matches = useMemo(() => {
    const needle = query.trim().toLowerCase()
    if (!needle) return options

    return options.filter((option) =>
      `${option.label} ${option.hint ?? ''} ${option.keywords ?? ''}`
        .toLowerCase()
        .includes(needle),
    )
  }, [options, query])

  /* Nothing matched, but the visitor has typed something real. Their own
     words become the last row rather than a dead end — a booking must never
     be blocked because somebody lives somewhere we forgot to seed. */
  const custom = query.trim()
  const rows: ComboboxOption[] =
    matches.length === 0 && custom
      ? [{ value: custom, label: custom, hint: 'Use as typed' }]
      : matches

  const close = (restoreFocus = true) => {
    setOpen(false)
    setQuery('')
    setActive(0)
    if (restoreFocus) trigger.current?.focus()
  }

  const commit = (option: ComboboxOption) => {
    onChange(option.value)
    close()
  }

  /* Focus moves into the search box on open, which is the point of opening.
     Done in an effect rather than at the click, because the input does not
     exist until the panel has rendered. */
  useEffect(() => {
    if (open) search.current?.focus()
  }, [open])

  /* A click anywhere else closes it. Capture phase, so it is seen before a
     click on something that then unmounts. Focus is not pulled back to the
     trigger here — the visitor is already on their way somewhere else. */
  useEffect(() => {
    if (!open) return

    const onPointerDown = (event: PointerEvent) => {
      if (!root.current?.contains(event.target as Node)) close(false)
    }

    document.addEventListener('pointerdown', onPointerDown, true)
    return () => document.removeEventListener('pointerdown', onPointerDown, true)
  }, [open])

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault()
      if (!open) return setOpen(true)
      if (rows.length === 0) return

      const step = event.key === 'ArrowDown' ? 1 : -1
      /* Wraps, so holding one arrow never dead-ends at an edge. */
      setActive((index) => (index + step + rows.length) % rows.length)
      return
    }

    if (event.key === 'Enter') {
      if (!open) return
      event.preventDefault()
      if (rows[active]) commit(rows[active])
      return
    }

    if (event.key === 'Escape' && open) {
      /* Stopped here so Escape closes the list rather than the dialog this
         field sits in — one Escape, one dismissal. */
      event.preventDefault()
      event.stopPropagation()
      close()
    }
  }

  return (
    <div ref={root} className={`relative ${className}`}>
      <button
        ref={trigger}
        id={id}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        onClick={() => (open ? close() : setOpen(true))}
        onKeyDown={onKeyDown}
        className={`mt-2 flex w-full items-center gap-2 rounded-xl border bg-raised px-4 py-3 text-left text-[15px] transition-colors duration-150 focus:outline-none ${
          open ? 'border-lime' : 'border-line hover:border-lime/40'
        }`}
        {...aria}
      >
        <span className={`min-w-0 flex-1 truncate ${value ? 'text-platinum' : 'text-dim'}`}>
          {value || placeholder}
        </span>
        <ChevronDown
          aria-hidden="true"
          strokeWidth={1.8}
          className={`size-4 shrink-0 text-lime-deep transition-transform duration-200 ${
            open ? 'rotate-180' : ''
          }`}
        />
      </button>

      {open ? (
        <div
          /* Free to be wider than the field it hangs off — the field may be
             half a grid row, and a name clipped to "Isla…" in the list you
             are choosing from defeats the point of the list. Capped so it
             stays inside the dialog on the narrowest phone. */
          className="absolute z-20 mt-1.5 w-max max-w-[min(22rem,78vw)] min-w-full overflow-hidden rounded-xl border border-line bg-elevated shadow-lift"
        >
          <div className="flex items-center gap-2.5 border-b border-line px-3.5 py-2.5">
            <Search
              aria-hidden="true"
              strokeWidth={1.8}
              className="size-4 shrink-0 text-dim"
            />
            <input
              ref={search}
              type="text"
              /* No "city" in the name, the id or the placeholder: those are
                 what Chrome's address heuristic reads, and its autofill
                 panel drawing over this list is the bug this replaced. */
              name="combobox-q"
              autoComplete="off"
              spellCheck={false}
              data-lpignore="true"
              data-1p-ignore=""
              role="combobox"
              aria-expanded
              aria-controls={listId}
              aria-autocomplete="list"
              aria-label="Search the list"
              aria-activedescendant={rows[active] ? `${listId}-${active}` : undefined}
              value={query}
              placeholder={searchPlaceholder}
              onChange={(event) => {
                setQuery(event.target.value)
                setActive(0)
              }}
              onKeyDown={onKeyDown}
              className="w-full bg-transparent text-[14px] text-platinum placeholder:text-dim focus:outline-none"
            />
          </div>

          <ul id={listId} role="listbox" aria-label="Options" className="max-h-52 overflow-y-auto py-1">
            {loading ? (
              <li className="px-4 py-3 text-[13px] text-dim">Loading…</li>
            ) : rows.length === 0 ? (
              <li className="px-4 py-3 text-[13px] text-steel">Nothing matches.</li>
            ) : (
              rows.map((option, index) => {
                const selected = option.value === value
                return (
                  <li key={option.value}>
                    <button
                      type="button"
                      id={`${listId}-${index}`}
                      role="option"
                      aria-selected={selected}
                      /* The pointer moves the cursor rather than a second
                         highlight following it, so mouse and keyboard are
                         never both claiming to be "here". */
                      onPointerEnter={() => setActive(index)}
                      onClick={() => commit(option)}
                      className={`flex w-full items-center gap-2 px-4 py-2.5 text-left text-[14px] transition-colors ${
                        index === active ? 'bg-lime-ink' : ''
                      } ${selected ? 'text-lime-deep' : 'text-silver'}`}
                    >
                      {/* Both can shrink, so a long region gives way before
                          the city name does — flexbox takes it from
                          whichever is longer, which is the region. */}
                      <span
                        className={`min-w-0 flex-1 truncate ${selected ? 'font-semibold' : ''}`}
                      >
                        {option.label}
                      </span>
                      {option.hint ? (
                        <span className="min-w-0 truncate text-[11.5px] text-dim">
                          {option.hint}
                        </span>
                      ) : null}
                      {/* The tick is the selected state, not the colour —
                          the row a colour-blind reader lands on still says
                          which one is theirs. */}
                      <Check
                        aria-hidden="true"
                        strokeWidth={2.6}
                        className={`size-4 shrink-0 text-lime-deep ${
                          selected ? '' : 'invisible'
                        }`}
                      />
                    </button>
                  </li>
                )
              })
            )}
          </ul>
        </div>
      ) : null}
    </div>
  )
}

export default Combobox
